/**
 * The diagram canvas.
 *
 * Owns the viewport, draws the graph, and reports selection back up. It is a
 * pure view of a `FlowGraph` — it never mutates one. Every visual change that
 * carries meaning goes through the code; the only state that lives here is
 * presentation (where you are looking, what is hovered).
 *
 * Performance notes: nodes and edges are individually memoised, and past
 * `CULL_THRESHOLD` elements anything outside the visible rectangle is skipped
 * entirely. Zoom and pan change one transform attribute, so neither triggers a
 * re-render of the children.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { FlowGraph, Point, Rect } from '../graph/model/types';
import { nodeRect } from '../graph/model/types';
import { DiagramNode } from './DiagramNode';
import { DiagramEdge } from './DiagramEdge';
import { Minimap } from './Minimap';
import { rectsIntersect, useViewport, visibleRect } from './useViewport';
import type { Viewport } from './useViewport';

/** Above this many drawables, cull anything off screen. */
const CULL_THRESHOLD = 250;

export interface DiagramViewHandle {
  fitToScreen(): void;
  zoomIn(): void;
  zoomOut(): void;
  resetZoom(): void;
  revealNode(nodeId: string): void;
  /** The live `<svg>` element — used by the "export visible area" path. */
  getSvgElement(): SVGSVGElement | null;
  getViewport(): Viewport;
  getVisibleRect(): Rect | null;
}

export interface DiagramLabels {
  readonly canvas: string;
  readonly minimap: string;
  readonly emptyTitle: string;
  readonly emptyBody: string;
}

export interface DiagramViewProps {
  readonly graph: FlowGraph;
  readonly selectedNodeId: string | null;
  readonly selectedEdgeId: string | null;
  /** Extra nodes to highlight, e.g. every call site of the selected function. */
  readonly highlightedNodeIds?: ReadonlySet<string>;
  readonly showGrid: boolean;
  readonly showMinimap: boolean;
  readonly reducedMotion: boolean;
  readonly labels: DiagramLabels;
  readonly onSelectNode: (id: string | null) => void;
  readonly onSelectEdge: (id: string | null) => void;
  /** Double-click or Enter: jump to the node's source. */
  readonly onActivateNode: (id: string) => void;
  readonly className?: string;
}

export const DiagramView = forwardRef<DiagramViewHandle, DiagramViewProps>(function DiagramView(
  {
    graph,
    selectedNodeId,
    selectedEdgeId,
    highlightedNodeIds,
    showGrid,
    showMinimap,
    reducedMotion,
    labels,
    onSelectNode,
    onSelectEdge,
    onActivateNode,
    className,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);

  const viewportController = useViewport({
    containerRef,
    bounds: graph.bounds,
    reducedMotion,
  });
  const { viewport, isPanning, handlers, fitToScreen, centerOn, revealRect } = viewportController;

  /* --- container measurement ----------------------------------------- */

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const update = (): void => {
      setSize({ width: element.clientWidth, height: element.clientHeight });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  /* --- automatic fit ---------------------------------------------------- */

  /**
   * Refit when the diagram is a genuinely different one, never while editing.
   *
   * "Different" is decided by node identity: an edit keeps most ids, whereas
   * loading an example, importing a file or starting a new project replaces
   * every one of them. Refitting on each keystroke would fight the user for
   * control of the viewport; not refitting at all leaves a freshly loaded
   * document stranded off screen at the previous document's zoom.
   */
  const previousIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (size.width === 0) return;

    const ids = new Set(graph.nodes.map((node) => node.id));
    const previous = previousIds.current;
    previousIds.current = ids;

    if (ids.size === 0) return;

    let shared = 0;
    for (const id of ids) if (previous.has(id)) shared += 1;

    const firstDiagram = previous.size === 0;
    const wholesaleReplacement = shared === 0;
    if (firstDiagram || wholesaleReplacement) fitToScreen();
  }, [graph.nodes, size.width, fitToScreen]);

  /* --- imperative API -------------------------------------------------- */

  const nodesById = useMemo(
    () => new Map(graph.nodes.map((node) => [node.id, node])),
    [graph.nodes],
  );

  useImperativeHandle(
    ref,
    () => ({
      fitToScreen,
      zoomIn: viewportController.zoomIn,
      zoomOut: viewportController.zoomOut,
      resetZoom: viewportController.resetZoom,
      revealNode: (nodeId: string) => {
        const node = nodesById.get(nodeId);
        if (node) revealRect(nodeRect(node));
      },
      getSvgElement: () => svgRef.current,
      getViewport: () => viewport,
      getVisibleRect: () =>
        size.width > 0 ? visibleRect(viewport, size.width, size.height, 0) : null,
    }),
    [fitToScreen, nodesById, revealRect, size, viewport, viewportController],
  );

  /* --- keep the selected node in view --------------------------------- */

  const lastRevealed = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedNodeId || selectedNodeId === lastRevealed.current) return;
    lastRevealed.current = selectedNodeId;
    const node = nodesById.get(selectedNodeId);
    if (!node || size.width === 0) return;
    // Only scroll if it is actually off screen — yanking the view on every
    // cursor move in the editor would be maddening.
    const visible = visibleRect(viewport, size.width, size.height, -40);
    if (!rectsIntersect(visible, nodeRect(node))) {
      revealRect(nodeRect(node));
    }
  }, [selectedNodeId, nodesById, revealRect, size, viewport]);

  /* --- culling --------------------------------------------------------- */

  const drawables = graph.nodes.length + graph.edges.length;
  const cull = drawables > CULL_THRESHOLD && size.width > 0;
  const window = cull ? visibleRect(viewport, size.width, size.height) : null;

  const visibleNodes = useMemo(() => {
    if (!window) return graph.nodes;
    return graph.nodes.filter((node) => rectsIntersect(window, nodeRect(node)));
  }, [graph.nodes, window]);

  const visibleEdges = useMemo(() => {
    if (!window) return graph.edges;
    return graph.edges.filter((edge) => rectsIntersect(window, edgeBounds(edge.points)));
  }, [graph.edges, window]);

  /* --- highlighting ---------------------------------------------------- */

  // When a node is selected, the edges touching it are emphasised and
  // everything else is dimmed, which is how you follow one path through a busy
  // diagram.
  const focusNodeId = selectedNodeId ?? hoveredNodeId;
  const connectedEdgeIds = useMemo(() => {
    if (!focusNodeId) return null;
    const ids = new Set<string>();
    for (const edge of graph.edges) {
      if (edge.source === focusNodeId || edge.target === focusNodeId) ids.add(edge.id);
    }
    return ids;
  }, [focusNodeId, graph.edges]);

  const neighbourNodeIds = useMemo(() => {
    if (!focusNodeId) return null;
    const ids = new Set<string>([focusNodeId]);
    for (const edge of graph.edges) {
      if (edge.source === focusNodeId) ids.add(edge.target);
      if (edge.target === focusNodeId) ids.add(edge.source);
    }
    if (highlightedNodeIds) for (const id of highlightedNodeIds) ids.add(id);
    return ids;
  }, [focusNodeId, graph.edges, highlightedNodeIds]);

  const handleBackgroundClick = useCallback(() => {
    onSelectNode(null);
    onSelectEdge(null);
  }, [onSelectNode, onSelectEdge]);

  const handleSelectNode = useCallback(
    (id: string) => {
      onSelectNode(id);
      onSelectEdge(null);
    },
    [onSelectNode, onSelectEdge],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const step = event.shiftKey ? 160 : 48;
      switch (event.key) {
        case 'ArrowUp':
          viewportController.setViewport({ ...viewport, y: viewport.y + step });
          break;
        case 'ArrowDown':
          viewportController.setViewport({ ...viewport, y: viewport.y - step });
          break;
        case 'ArrowLeft':
          viewportController.setViewport({ ...viewport, x: viewport.x + step });
          break;
        case 'ArrowRight':
          viewportController.setViewport({ ...viewport, x: viewport.x - step });
          break;
        case '+':
        case '=':
          viewportController.zoomIn();
          break;
        case '-':
        case '_':
          viewportController.zoomOut();
          break;
        case '0':
          fitToScreen();
          break;
        case 'Escape':
          handleBackgroundClick();
          return;
        default:
          return;
      }
      event.preventDefault();
    },
    [fitToScreen, handleBackgroundClick, viewport, viewportController],
  );

  const empty = graph.nodes.length === 0;

  return (
    <div
      ref={containerRef}
      className={`relative h-full w-full overflow-hidden outline-none ${className ?? ''}`}
      style={{
        background: 'var(--fs-diagram-bg)',
        touchAction: 'none',
        cursor: isPanning ? 'grabbing' : 'grab',
      }}
      tabIndex={0}
      role="application"
      aria-label={labels.canvas}
      onKeyDown={handleKeyDown}
      {...handlers}
      onClick={handleBackgroundClick}
    >
      {empty ? (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2 px-8 text-center">
          <p className="text-base font-medium text-ink-muted">{labels.emptyTitle}</p>
          <p className="max-w-sm text-sm text-ink-faint">{labels.emptyBody}</p>
        </div>
      ) : null}

      <svg ref={svgRef} width="100%" height="100%" role="presentation" style={{ display: 'block' }}>
        {showGrid ? <GridPattern viewport={viewport} /> : null}

        <g transform={`translate(${viewport.x} ${viewport.y}) scale(${viewport.scale})`}>
          {graph.groups.map((group) =>
            group.bounds ? (
              <g key={group.id} aria-hidden="true">
                <rect
                  x={group.bounds.x}
                  y={group.bounds.y}
                  width={group.bounds.width}
                  height={group.bounds.height}
                  rx={12}
                  fill="var(--fs-group-fill)"
                  fillOpacity={0.35}
                  stroke="var(--fs-group-stroke)"
                  strokeWidth={1.25}
                  strokeDasharray="6 5"
                />
                <text
                  x={group.bounds.x + 12}
                  y={group.bounds.y + 15}
                  fontSize={11}
                  fontWeight={600}
                  fill="var(--fs-group-label)"
                  style={{ userSelect: 'none' }}
                >
                  {group.label}
                </text>
              </g>
            ) : null,
          )}

          {visibleEdges.map((edge) => (
            <DiagramEdge
              key={edge.id}
              edge={edge}
              selected={edge.id === selectedEdgeId}
              highlighted={connectedEdgeIds?.has(edge.id) ?? edge.id === hoveredEdgeId}
              muted={connectedEdgeIds !== null && !connectedEdgeIds.has(edge.id)}
              cornerRadius={6}
              fontSize={12}
              onSelect={onSelectEdge}
              onHover={setHoveredEdgeId}
            />
          ))}

          {visibleNodes.map((node) => (
            <DiagramNode
              key={node.id}
              node={node}
              selected={node.id === selectedNodeId}
              hovered={node.id === hoveredNodeId || (highlightedNodeIds?.has(node.id) ?? false)}
              muted={neighbourNodeIds !== null && !neighbourNodeIds.has(node.id)}
              fontSize={14}
              lineHeight={19}
              onSelect={handleSelectNode}
              onActivate={onActivateNode}
              onHover={setHoveredNodeId}
            />
          ))}
        </g>
      </svg>

      {showMinimap && !empty ? (
        <div className="pointer-events-auto absolute right-3 bottom-3 hidden md:block">
          <Minimap
            graph={graph}
            viewport={viewport}
            containerWidth={size.width}
            containerHeight={size.height}
            label={labels.minimap}
            onNavigate={(point: Point) => centerOn({ x: point.x, y: point.y, width: 0, height: 0 })}
          />
        </div>
      ) : null}
    </div>
  );
});

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

function GridPattern({ viewport }: { viewport: Viewport }) {
  const step = 24 * viewport.scale;
  // Below a few pixels the grid turns into a solid wash; hide it instead.
  if (step < 6) return null;
  return (
    <>
      <defs>
        <pattern
          id="fs-grid"
          width={step}
          height={step}
          patternUnits="userSpaceOnUse"
          x={viewport.x % step}
          y={viewport.y % step}
        >
          <circle cx={0.5} cy={0.5} r={0.9} fill="var(--fs-grid)" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#fs-grid)" />
    </>
  );
}

function edgeBounds(points: readonly Point[]): Rect {
  if (points.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
